import { cn } from '@ps/ui/utils';

import {
  formatTipGoalAmountCompact,
  tipGoalProgressPercent,
} from '../../functions/tipGoalDisplay';
import type { TippingGoalSummary } from '../../types/TippingGoalSummary';
import { AnimatedHeartIcon } from '../AnimatedHeartIcon';

interface Props {
  /** Default pill label, shown when there's no active goal. */
  label: string;
  /** The active goal — switches to the two-line progress-fill pill. */
  goal?: TippingGoalSummary | null;
  className?: string;
}

/**
 * Pure presentational tipping header pill — the visual for BOTH the streaming
 * Suspense fallback (static, server) and the interactive client button. No
 * interaction of its own.
 *
 *   - no goal → single-line pill (emitting heart + label).
 *   - active goal → two-line pill with a green progress fill behind
 *     `buttonText` + `<achieved> / <target>` (compact). Progress % / compact
 *     amounts derived here at render.
 *
 * Neither variant sets `overflow-hidden` — the {@link AnimatedHeartIcon} particles
 * rise above the pill. The goal fill is clipped in its OWN rounded layer instead.
 */
export function TippingHeaderPillView({ label, goal, className }: Props) {
  if (!goal) {
    return (
      <span
        className={cn(
          'bg-surface-canvas relative flex items-center gap-2 whitespace-nowrap rounded-full py-1.5 pl-2.5 pr-4 shadow-md backdrop-blur',
          className,
        )}
      >
        <AnimatedHeartIcon size={18} className="shrink-0" />
        <span className="text-brand-text text-[12px] font-medium">{label}</span>
      </span>
    );
  }

  const percent = tipGoalProgressPercent(
    goal.achievedAmount,
    goal.targetAmount,
  );

  return (
    <span
      className={cn(
        'border-raise/40 bg-surface-canvas relative flex items-center gap-2 whitespace-nowrap rounded-full border py-1.5 pl-2.5 pr-5 shadow-md backdrop-blur',
        className,
      )}
    >
      {/* Progress fill — clipped to the pill shape in its OWN layer so the pill
          itself stays `overflow: visible` and the rising hearts aren't cut off. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-full"
      >
        <span
          className="absolute inset-y-0 left-0 bg-green-700 transition-[width] duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
      </span>

      <AnimatedHeartIcon size={18} className="relative z-[1] shrink-0" />
      <span className="relative z-[1] flex flex-col leading-tight">
        <span className="text-brand-text max-w-[180px] truncate text-[12px] font-medium">
          {goal.buttonText}
        </span>
        <span className="text-[12px]">
          <span className="text-content-1 font-medium">
            {formatTipGoalAmountCompact(goal.achievedAmount)}
          </span>
          <span className="text-content-2 font-light">
            {' / '}
            {formatTipGoalAmountCompact(goal.targetAmount)}
          </span>
        </span>
      </span>
    </span>
  );
}
